import React, { useEffect, useMemo, useState } from 'react';
import Notice from './Notice';
import { confirmDialog } from './ConfirmDialog';

// 確認待ち（名簿外・時間外）の打刻をまとめて確定する【2026-10-01】
//   打刻はされたが「確認待ち」になっていて、出席率に入っていないもの。
//   チェックを付けて「出席にする」「遅刻にする」「欠席にする」をまとめて押せる。
//   名簿に載っていない学生は「名簿にも入れる」を付けると、次の回からは普通に出席になる。
// 【2026-10-07】classId を渡すとその授業の分だけ（授業の列を出さない）。確定したら onChanged を呼ぶ
export default function PendingAttendancePane({ classId = null, onChanged = null }) {
  const [items, setItems]     = useState([]);
  const [checked, setChecked] = useState({});
  const [enroll, setEnroll]   = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch('/api/attendance/pending' + (classId ? '?classId=' + encodeURIComponent(classId) : ''), { headers: auth() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '取得に失敗しました');
      setItems(data.items || []);
      setChecked({});
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [classId]);

  const ids = useMemo(() => items.filter(x => checked[x.id]).map(x => x.id), [items, checked]);
  const allOn = items.length > 0 && ids.length === items.length;
  const hasOutside = items.some(x => checked[x.id] && !x.enrolled);

  const resolve = async (action, status) => {
    if (!ids.length) { setErr('確定する打刻にチェックを付けてください'); return; }
    const label = action === 'absent' ? '欠席' : status === 'late' ? '遅刻' : '出席';
    if (!(await confirmDialog(`${ids.length}件を「${label}」に確定します。よろしいですか？`))) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/attendance/pending/resolve', {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, action, status, enroll: action === 'approve' && enroll }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '確定に失敗しました');
      setMsg(data.message);
      if (data.skipped && data.skipped.length) setErr(data.skipped.map(s => `#${s.id}: ${s.message}`).join(' / '));
      await load();
      if (onChanged) onChanged();
      // 要対応のバッジに知らせる【2026-11-02】
      try { window.dispatchEvent(new Event('action-items-changed')); } catch (e) { /* 何もしない */ }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const day = (ymd) => (ymd ? `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}` : '');

  return (
    <div>
      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>
        名簿に載っていない学生の打刻や、授業時間外の打刻は「確認待ち」で記録され、出席率に入っていません。
        確かめてから、まとめて確定してください。
      </p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0.5rem 0' }}>
        <button className="btn-primary" disabled={busy || !ids.length} onClick={() => resolve('approve', 'present')}>出席にする</button>
        <button className="btn-secondary" disabled={busy || !ids.length} onClick={() => resolve('approve', 'late')}>遅刻にする</button>
        <button disabled={busy || !ids.length} onClick={() => resolve('absent')} style={{ color: '#b91c1c' }}>欠席にする</button>
        <label style={{ fontSize: '0.85rem', opacity: hasOutside ? 1 : 0.5 }}>
          <input type="checkbox" checked={enroll} onChange={e => setEnroll(e.target.checked)} />
          {' '}名簿に無い学生は名簿にも入れる
        </label>
        <span style={{ fontSize: '0.85rem', color: '#555' }}>{ids.length}件を選択中</span>
        <button className="btn-secondary" onClick={load} disabled={loading} style={{ marginLeft: 'auto' }}>
          {loading ? '読み込み中…' : '再読み込み'}
        </button>
      </div>

      <Notice kind="error" compact>{err}</Notice>
      <Notice kind="success" toast>{msg}</Notice>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={allOn} disabled={!items.length}
                       onChange={e => setChecked(e.target.checked ? Object.fromEntries(items.map(x => [x.id, true])) : {})} />
              </th>
              <th>授業日</th>{!classId && <th>授業</th>}<th>学生</th><th>理由</th><th>打刻</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={classId ? 5 : 6} style={{ textAlign: 'center', color: '#888' }}>
                {loading ? '読み込み中…' : '確認待ちの打刻はありません'}
              </td></tr>
            )}
            {items.map(x => (
              <tr key={x.id} onClick={() => setChecked(c => ({ ...c, [x.id]: !c[x.id] }))} style={{ cursor: 'pointer' }}>
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={!!checked[x.id]}
                         onChange={e => setChecked(c => ({ ...c, [x.id]: e.target.checked }))} />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{day(x.sessionDate)}{x.sessionStart ? ` ${x.sessionStart}` : ''}</td>
                {!classId && <td>{x.className}</td>}
                <td>{x.studentName}<div style={{ fontSize: '0.75rem', color: '#888' }}>{x.studentId}</div></td>
                <td style={{ fontSize: '0.85rem', color: x.enrolled ? '#92400e' : '#b91c1c' }}>{x.reasonLabel}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{fmt(x.recordedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
